// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 39-gcp-installation.md
// @greenlight-spec: decisions/0031-shared-wizard-settings-editors.md

import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { StepHeader } from '@/components/install/StepHeader';
import {
  ContactDisclosure,
  ContactEditor,
  useContactEditor,
} from '@/components/greenlight/org/ContactEditor';
import {
  DisplayNameEditor,
  useDisplayNameEditor,
} from '@/components/greenlight/org/DisplayNameEditor';

interface OrgSetupStepProps {
  onCreate: (input: {
    name: string;
    contact_name: string;
    contact_email: string;
  }) => Promise<boolean>;
  isSubmitting: boolean;
}

export function OrgSetupStep({ onCreate, isSubmitting }: OrgSetupStepProps) {
  const displayNameEditor = useDisplayNameEditor('');
  const contactEditor = useContactEditor('', '');
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = displayNameEditor.validate();
    const contact = contactEditor.validate();
    if (trimmedName === null || contact === null) return;
    const ok = await onCreate({ name: trimmedName, ...contact });
    if (!ok)
      setError('Could not create the organization. It may already exist — refresh and continue.');
  };

  return (
    // `noValidate`: the email input's type is for the mobile keyboard; the
    // contact editor reports its own problems in one styled message.
    <form className="space-y-6" noValidate onSubmit={handleSubmit} data-testid="step-org_setup">
      <StepHeader step="org_setup" title="Set up your organization">
        Greenlight Infrastructure has been provisioned. Finish setting up your installation.
      </StepHeader>

      <DisplayNameEditor
        editor={displayNameEditor}
        id="org-name"
        inputTestId="org-setup-name"
        disabled={isSubmitting}
      />

      <section className="space-y-4 rounded-lg border p-4" data-testid="org-setup-contact">
        <div>
          <h2 className="text-sm font-semibold">Contact info</h2>
          <p className="text-sm text-muted-foreground">
            In case Shift needs to contact you about this install&rsquo;s subscription and billing.
          </p>
        </div>
        <ContactEditor editor={contactEditor} disabled={isSubmitting} idPrefix="org-contact" />
        <ContactDisclosure />
      </section>

      {contactEditor.error && (
        <Alert variant="destructive" data-testid="contact-error">
          <AlertDescription>{contactEditor.error}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full md:w-auto h-11 md:h-9"
        disabled={isSubmitting}
        data-testid="org-setup-submit"
      >
        {isSubmitting ? 'Creating…' : 'Create organization'}
      </Button>
    </form>
  );
}
